import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { formMappings } from "../../../../config/formMappings";
import { FormEntry } from "../../../../types/types";
import { useState } from "react";
import { classNames } from "../../../../utils/utils";
import { Link } from "react-router-dom";
import { getRegistrantsContainer } from "../../../../utils/utils";

interface TransportationRequestTableProps {
  v2Entries: FormEntry[];
}

// Helper function to escape CSV values
function escapeCSV(value: string): string {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export default function TransportationRequestTable({
  v2Entries,
}: TransportationRequestTableProps) {
  const [isExporting, setIsExporting] = useState(false);
  const activeFormName = useSelector(
    (state: RootState) => state.activeForm.activeFormName
  );
  const allEntries = useSelector(
    (state: RootState) => state.formEntries.allEntries
  );
  const formMapping = formMappings[activeFormName as keyof typeof formMappings];
  const transportationRequestMapping =
    formMapping && "transportationRequest" in formMapping
      ? formMapping.transportationRequest
      : undefined;

  // Helper function to find registrant entry by email (only paid entries)
  const findRegistrantEntryByEmail = (email: string): string | null => {
    if (!email || email === "N/A") return null;

    const emailLower = email.toLowerCase();
    const emailFieldKey = formMapping?.fieldMappings?.email || "jgfy3";
    const paidFieldKey = formMapping?.fieldMappings?.paid;

    // Collect all matching paid entries
    const matchingPaidEntries: FormEntry[] = [];

    for (const entry of allEntries) {
      // Check if entry is paid
      const paidStatusRaw = paidFieldKey
        ? entry.meta[paidFieldKey] || entry.meta["Paid?"]
        : entry.meta["Paid?"];
      const paidStatus = Array.isArray(paidStatusRaw)
        ? paidStatusRaw[0]
        : paidStatusRaw;

      if (paidStatus !== "Yes") {
        continue; // Skip unpaid entries
      }

      const emails: string[] = [];

      // Check if Email exists at entry level
      if (entry.meta.Email) {
        if (Array.isArray(entry.meta.Email)) {
          emails.push(...entry.meta.Email.map((e) => String(e).toLowerCase()));
        } else {
          emails.push(String(entry.meta.Email).toLowerCase());
        }
      }

      // Extract emails from registrants
      const registrantsContainer = getRegistrantsContainer(entry.meta);
      if (registrantsContainer) {
        Object.entries(registrantsContainer)
          .filter(([key]) => key !== "form")
          .forEach(([_, registrant]: [string, any]) => {
            const registrantEmail = registrant[emailFieldKey];
            if (registrantEmail) {
              if (Array.isArray(registrantEmail)) {
                emails.push(
                  ...registrantEmail.map((e) => String(e).toLowerCase())
                );
              } else {
                emails.push(String(registrantEmail).toLowerCase());
              }
            }
          });
      }

      // Check if email matches
      if (emails.includes(emailLower)) {
        matchingPaidEntries.push(entry);
      }
    }

    // If no paid entries found, return null
    if (matchingPaidEntries.length === 0) {
      return null;
    }

    // If multiple paid entries, prioritize the most recent one
    if (matchingPaidEntries.length > 1) {
      matchingPaidEntries.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    }

    return matchingPaidEntries[0].item_key;
  };

  // Process v2 entries using transportation request field mappings
  const transportationRequests = v2Entries.map((entry) => {
    if (!transportationRequestMapping) {
      return {
        firstName: "N/A",
        lastName: "N/A",
        email: "N/A",
        phoneNumber: "N/A",
        numberOfPeople: "N/A",
        numberOfLuggage: "N/A",
        paid: "No",
        registrantItemKey: null,
      };
    }

    const firstNameRaw =
      entry.meta[transportationRequestMapping.fieldMappings.firstName] ||
      entry.meta["First Name"] ||
      "N/A";
    const lastNameRaw =
      entry.meta[transportationRequestMapping.fieldMappings.lastName] ||
      entry.meta["Last Name"] ||
      "N/A";
    const emailRaw =
      entry.meta[transportationRequestMapping.fieldMappings.email] ||
      entry.meta["Email"] ||
      "N/A";
    const phoneRaw =
      entry.meta[transportationRequestMapping.fieldMappings.phone] ||
      entry.meta["Phone"] ||
      "N/A";
    const numberOfPeopleRaw =
      entry.meta[transportationRequestMapping.fieldMappings.numberOfPeople] ||
      "N/A";
    const luggageCountRaw =
      entry.meta[transportationRequestMapping.fieldMappings.luggageCount] ||
      "N/A";
    const paidStatusRaw =
      entry.meta[transportationRequestMapping.fieldMappings.paid] ||
      entry.meta["Paid?"] ||
      "No";

    // Handle array values
    const firstName = Array.isArray(firstNameRaw)
      ? firstNameRaw[0]
      : firstNameRaw;
    const lastName = Array.isArray(lastNameRaw) ? lastNameRaw[0] : lastNameRaw;
    const email = Array.isArray(emailRaw) ? emailRaw[0] : emailRaw;
    const phoneNumber = Array.isArray(phoneRaw) ? phoneRaw[0] : phoneRaw;
    const numberOfPeople = Array.isArray(numberOfPeopleRaw)
      ? numberOfPeopleRaw[0]
      : numberOfPeopleRaw;
    const numberOfLuggage = Array.isArray(luggageCountRaw)
      ? luggageCountRaw[0]
      : luggageCountRaw;
    const paidStatus = Array.isArray(paidStatusRaw)
      ? paidStatusRaw[0]
      : paidStatusRaw;

    // Additional fields for CSV export
    const needCarSeatRaw = transportationRequestMapping
      ? entry.meta[transportationRequestMapping.fieldMappings.needCarSeat] ||
        "N/A"
      : "N/A";
    const arrivalDateRaw = transportationRequestMapping
      ? entry.meta[transportationRequestMapping.fieldMappings.arrivalDate] ||
        "N/A"
      : "N/A";
    const arrivalTimeRaw = transportationRequestMapping
      ? entry.meta[transportationRequestMapping.fieldMappings.arrivalTime] ||
        "N/A"
      : "N/A";
    const arrivalFlightNumberRaw = transportationRequestMapping
      ? entry.meta[
          transportationRequestMapping.fieldMappings.arrivalFlightNumber
        ] || "N/A"
      : "N/A";
    const departureDateRaw = transportationRequestMapping
      ? entry.meta[transportationRequestMapping.fieldMappings.departureDate] ||
        "N/A"
      : "N/A";
    const departureTimeRaw = transportationRequestMapping
      ? entry.meta[transportationRequestMapping.fieldMappings.departureTime] ||
        "N/A"
      : "N/A";
    const departureFlightNumberRaw = transportationRequestMapping
      ? entry.meta[
          transportationRequestMapping.fieldMappings.departureFlightNumber
        ] || "N/A"
      : "N/A";
    const totalCostRaw = transportationRequestMapping
      ? entry.meta[transportationRequestMapping.fieldMappings.totalCost] ||
        "N/A"
      : "N/A";

    // Find matching registrant entry by email
    const registrantItemKey = findRegistrantEntryByEmail(String(email || ""));

    return {
      firstName: String(firstName || "N/A"),
      lastName: String(lastName || "N/A"),
      email: String(email || "N/A"),
      phoneNumber: String(phoneNumber || "N/A"),
      numberOfPeople: String(numberOfPeople || "N/A"),
      numberOfLuggage: String(numberOfLuggage || "N/A"),
      paid: paidStatus === "Yes" ? "Yes" : "No",
      registrantItemKey: registrantItemKey,
      // Additional fields for CSV export
      needCarSeat: needCarSeatRaw,
      arrivalDate: arrivalDateRaw,
      arrivalTime: arrivalTimeRaw,
      arrivalFlightNumber: arrivalFlightNumberRaw,
      departureDate: departureDateRaw,
      departureTime: departureTimeRaw,
      departureFlightNumber: departureFlightNumberRaw,
      totalCost: totalCostRaw,
      entryId: entry.id || "N/A",
      createdAt: entry.created_at || "N/A",
    };
  });

  // CSV Export function
  const handleExportCSV = () => {
    setIsExporting(true);

    // Use setTimeout to ensure the UI updates before the heavy processing
    setTimeout(() => {
      const csvRows: string[] = [];

      // Define CSV headers with all available fields
      const headers = [
        "Entry ID",
        "First Name",
        "Last Name",
        "Email",
        "Phone Number",
        "Number of People",
        "Number of Luggage",
        "Need Car Seat",
        "Arrival Date",
        "Arrival Time",
        "Arrival Flight Number",
        "Departure Date",
        "Departure Time",
        "Departure Flight Number",
        "Total Cost",
        "Paid",
        "Created At",
      ];

      csvRows.push(headers.map(escapeCSV).join(","));

      // Process each entry
      transportationRequests.forEach((entry) => {
        const row = [
          String(entry.entryId || "N/A"),
          entry.firstName,
          entry.lastName,
          entry.email,
          entry.phoneNumber,
          entry.numberOfPeople,
          entry.numberOfLuggage,
          String(
            Array.isArray(entry.needCarSeat)
              ? entry.needCarSeat[0]
              : entry.needCarSeat || "N/A"
          ),
          String(
            Array.isArray(entry.arrivalDate)
              ? entry.arrivalDate[0]
              : entry.arrivalDate || "N/A"
          ),
          String(
            Array.isArray(entry.arrivalTime)
              ? entry.arrivalTime[0]
              : entry.arrivalTime || "N/A"
          ),
          String(
            Array.isArray(entry.arrivalFlightNumber)
              ? entry.arrivalFlightNumber[0]
              : entry.arrivalFlightNumber || "N/A"
          ),
          String(
            Array.isArray(entry.departureDate)
              ? entry.departureDate[0]
              : entry.departureDate || "N/A"
          ),
          String(
            Array.isArray(entry.departureTime)
              ? entry.departureTime[0]
              : entry.departureTime || "N/A"
          ),
          String(
            Array.isArray(entry.departureFlightNumber)
              ? entry.departureFlightNumber[0]
              : entry.departureFlightNumber || "N/A"
          ),
          String(
            Array.isArray(entry.totalCost)
              ? entry.totalCost[0]
              : entry.totalCost || "N/A"
          ),
          entry.paid,
          String(entry.createdAt || "N/A"),
        ];

        csvRows.push(row.map(escapeCSV).join(","));
      });

      // Create CSV content and download
      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `transportation_requests_export_${
          new Date().toISOString().split("T")[0]
        }.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the object URL
      URL.revokeObjectURL(url);

      // Reset loading state after a short delay to ensure download started
      setTimeout(() => {
        setIsExporting(false);
      }, 500);
    }, 0);
  };

  return (
    <div className="overflow-y-scroll no-scrollbar rounded-lg bg-white shadow col-span-4">
      <div className="px-4 py-3 border-b border-gray-200 flex justify-end">
        <button
          type="button"
          onClick={handleExportCSV}
          disabled={isExporting}
          className={classNames(
            "block rounded-md px-3 py-2 text-center text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
            isExporting
              ? "bg-primary/70 cursor-not-allowed"
              : "bg-primary hover:bg-secondary focus-visible:outline-primary"
          )}
        >
          <span className="flex items-center justify-center gap-2">
            {isExporting && (
              <svg
                className="h-4 w-4 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            )}
            {isExporting ? "Exporting..." : "Export CSV"}
          </span>
        </button>
      </div>
      <table className="min-w-full divide-y divide-gray-300">
        <thead className="bg-gray-50">
          <tr>
            <th
              scope="col"
              className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
            >
              Name
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >
              Email
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >
              Phone Number
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >
              Number of People
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >
              Number of Luggage
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >
              Paid
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >
              View
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {transportationRequests.map((entry, idx) => (
            <tr key={idx}>
              <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                {entry.firstName} {entry.lastName}
              </td>
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                <a href={`mailto:${entry.email}`} className="hover:underline">
                  {entry.email}
                </a>
              </td>
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                <a
                  href={`tel:${entry.phoneNumber}`}
                  className="hover:underline"
                >
                  {entry.phoneNumber}
                </a>
              </td>
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                {entry.numberOfPeople}
              </td>
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                {entry.numberOfLuggage}
              </td>
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                <span
                  className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                    entry.paid === "Yes"
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {entry.paid}
                </span>
              </td>
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                {entry.registrantItemKey ? (
                  <Link
                    to={`/registrant/${entry.registrantItemKey}`}
                    className="text-primary hover:text-secondary font-medium"
                  >
                    View
                  </Link>
                ) : (
                  <span className="text-gray-400">N/A</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
